import React, { useState, useEffect } from "react";
import config from "../config";
import { processWrongGuess, HintCountdown, WordChain } from "./HintSystem";

function SinglePlayer() {
  const [words, setWords] = useState([]);
  const [guessedWords, setGuessedWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [guess, setGuess] = useState("");
  const [timer, setTimer] = useState(60);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [wrongAttemptsForHint, setWrongAttemptsForHint] = useState(0);
  const [hintCountdown, setHintCountdown] = useState("");
  const [wordHints, setWordHints] = useState({}); // Track hints for each word
  const [animatingWordIndex, setAnimatingWordIndex] = useState(-1);
  const [gameComplete, setGameComplete] = useState(false);
  const [finalTime, setFinalTime] = useState(0);

  const fetchWords = () => {
    fetch(`${config.API_BASE_URL}/api/single_player/words`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setWords(data);
        setGuessedWords([data[0]]);
      })
      .catch((error) => {
        console.error("Error fetching words:", error);
      });
  };

  const resetGame = () => {
    setGuessedWords([]);
    setCurrentWordIndex(0);
    setGuess("");
    setTimer(60);
    setWrongAttempts(0);
    setWrongAttemptsForHint(0);
    setHintCountdown("");
    setWordHints({});
    setAnimatingWordIndex(-1);
    setGameComplete(false);
    setFinalTime(0);
    fetchWords();
  };

  useEffect(() => {
    fetchWords();
  }, []);

  useEffect(() => {
    if (gameComplete) return; // Stop timer when game is complete

    const interval = setInterval(() => {
      setTimer((prevTimer) => (prevTimer > 0 ? prevTimer - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [gameComplete]);

  const handleGuess = (e) => {
    e.preventDefault();
    const correctWord = words[currentWordIndex + 1];
    if (guess.toLowerCase() === correctWord.toLowerCase()) {
      const newWordIndex = currentWordIndex + 1;
      setGuessedWords([...guessedWords, correctWord]);
      setCurrentWordIndex(newWordIndex);
      setAnimatingWordIndex(newWordIndex); // Trigger animation for the new word
      setGuess("");
      setHintCountdown("");
      setWrongAttempts(0);
      setWrongAttemptsForHint(0);

      // Check if game is complete
      if (newWordIndex >= words.length - 1) {
        setGameComplete(true);
        setFinalTime(timer);
      }

      // Clear animation after it completes
      setTimeout(() => setAnimatingWordIndex(-1), 800);
    } else {
      const newWrongAttempts = wrongAttempts + 1;
      const newWrongAttemptsForHint = wrongAttemptsForHint + 1;

      setWrongAttempts(newWrongAttempts);
      setWrongAttemptsForHint(newWrongAttemptsForHint);

      // Use the reusable hint system
      const wordIndex = currentWordIndex + 1;
      const currentHintLength = wordHints[String(wordIndex)] || 0;
      const hintResult = processWrongGuess(
        newWrongAttemptsForHint,
        correctWord,
        currentHintLength
      );

      if (hintResult.shouldGiveHint) {
        // Update hints for the current word using string key
        setWordHints((prev) => ({
          ...prev,
          [String(wordIndex)]: hintResult.newHintLength,
        }));
        setWrongAttemptsForHint(0); // Reset counter for next hint
        setHintCountdown(""); // Clear countdown when hint is shown
      } else {
        setHintCountdown(hintResult.message);
      }
    }
    setGuess(""); // Clear input after every guess
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && guess.trim() && !gameComplete && timer > 0) {
      handleGuess(e);
    }
  };

  return (
    <div>
      <h1>Word Dash (Solo)</h1>
      <div>Time Remaining: {timer}</div>

      <WordChain
        guessedWords={guessedWords}
        remainingWords={words.slice(currentWordIndex + 1)}
        wordHints={wordHints}
        currentProgress={currentWordIndex}
        animatingWordIndex={animatingWordIndex}
        isGameOver={timer === 0}
      />

      {currentWordIndex < words.length - 1 && timer > 0 && !gameComplete && (
        <form onSubmit={handleGuess}>
          <input
            type="text"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            onKeyUp={handleKeyPress}
            placeholder="Guess the next word"
            disabled={timer === 0 || gameComplete}
          />
          <button type="submit" disabled={timer === 0 || gameComplete}>
            Guess
          </button>
        </form>
      )}

      <HintCountdown message={hintCountdown} />

      {gameComplete && (
        <div className="game-complete-section">
          <h2 className="game-over" style={{ color: "#4CAF50" }}>
            🎉 Congratulations! You completed the chain! 🎉
          </h2>
          <p>
            Time completed with: <strong>{finalTime} seconds remaining</strong>
          </p>
          <button onClick={resetGame}>Start New Game</button>
        </div>
      )}

      {timer === 0 && !gameComplete && (
        <div className="game-complete-section">
          <h2 className="game-over">Game Over</h2>
          <button onClick={resetGame}>Try Again</button>
        </div>
      )}
    </div>
  );
}

export default SinglePlayer;
